import React, { useState, useRef } from 'react';
import { Button, Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { PlayFill, StopFill } from 'react-bootstrap-icons';

// Global state to track which audio is currently playing
let currentlyPlayingAudio = null;

const VoiceControl = ({ 
  voice, 
  onPlay, 
  onPause, 
  className = "", 
  variant = "outline-primary",
  size = "lg",
  genderColor = null
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  // Check if description needs truncation (more than 15 words)
  const needsTruncation = voice.description && voice.description.split(' ').length > 15;
  
  // Truncate description to 15 words
  const truncatedDescription = needsTruncation 
    ? voice.description.split(' ').slice(0, 15).join(' ') + '...'
    : voice.description;

  const handlePlayPause = (e) => {
    // Prevent the click from bubbling up to parent elements
    e.stopPropagation();
    
    if (isPlaying) {
      // Stop the audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0; // Reset to beginning
      }
      setIsPlaying(false);
      currentlyPlayingAudio = null;
      if (onPause) onPause(voice);
    } else {
      // Stop any currently playing audio first
      if (currentlyPlayingAudio && currentlyPlayingAudio !== audioRef.current) {
        currentlyPlayingAudio.pause();
        currentlyPlayingAudio.currentTime = 0;
        // Find and update the state of the previously playing component
        const event = new CustomEvent('audioStopped', { 
          detail: { audioRef: currentlyPlayingAudio } 
        });
        window.dispatchEvent(event);
      }
      
      // Play the audio
      if (audioRef.current) {
        audioRef.current.play();
      }
      setIsPlaying(true);
      currentlyPlayingAudio = audioRef.current;
      if (onPlay) onPlay(voice);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    currentlyPlayingAudio = null;
    if (onPause) onPause(voice);
  };

  const handleAudioError = (error) => {
    console.error('Audio playback error:', error);
    setIsPlaying(false);
    currentlyPlayingAudio = null;
    if (onPause) onPause(voice);
  };

  // Listen for audio stopped events from other components
  React.useEffect(() => {
    const handleAudioStopped = (event) => {
      // If this component's audio was stopped by another component, reset its state
      if (event.detail.audioRef === audioRef.current && isPlaying) {
        setIsPlaying(false);
      }
    };

    window.addEventListener('audioStopped', handleAudioStopped);
    return () => {
      window.removeEventListener('audioStopped', handleAudioStopped);
    };
  }, [isPlaying]);

  // Also listen for when this component's audio is interrupted
  React.useEffect(() => {
    const handleAudioInterrupted = () => {
      if (isPlaying && audioRef.current && currentlyPlayingAudio !== audioRef.current) {
        setIsPlaying(false);
      }
    };

    // Check periodically if this audio was interrupted
    const interval = setInterval(handleAudioInterrupted, 100);
    
    return () => {
      clearInterval(interval);
    };
  }, [isPlaying]);

  return (
    <Card className={`voice-control ${className}`} style={{ minHeight: '50px' }}>
      <Card.Body className="d-flex align-items-center py-2">
        {/* Circular Play/Pause Button */}
        <div className="me-2">
          <Button
            variant={variant}
            size={size}
            onClick={handlePlayPause}
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{ 
              width: '30px', 
              height: '30px',
              minWidth: '30px'
            }}
          >
            {isPlaying ? <StopFill size={12} /> : <PlayFill size={12} />}
          </Button>
        </div>
        
        {/* Voice Info */}
        <div className="flex-grow-1">
          <h6 
            className="fw-bold mb-0" 
            style={{ 
              fontSize: '0.85rem',
              color: 'black'
            }}
          >
            {voice.name}
            {voice.gender && (
              <span style={{ 
                color: voice.gender === 'male' ? '#007bff' : '#dc3545',
                fontWeight: 'bold'
              }}>
                {voice.gender === 'male' ? ' (M)' : ' (F)'}
              </span>
            )}
          </h6>
          {voice.description && (
            needsTruncation ? (
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip 
                    id={`tooltip-${voice.id}`}
                    className="wide-tooltip"
                  >
                    {voice.description}
                  </Tooltip>
                }
              >
                <p 
                  className="text-muted mb-0" 
                  style={{ 
                    fontSize: '0.7rem',
                    lineHeight: '1.2',
                    paddingTop: '2px'
                  }}
                >
                  {truncatedDescription}
                </p>
              </OverlayTrigger>
            ) : (
              <p 
                className="text-muted mb-0" 
                style={{ 
                  fontSize: '0.7rem',
                  lineHeight: '1.2',
                  paddingTop: '2px'
                }}
              >
                {voice.description}
              </p>
            )
          )}
        </div>
        
        {/* Hidden Audio Element */}
        {voice.sampleUrl && (
          <audio
            ref={audioRef}
            src={voice.sampleUrl}
            onEnded={handleAudioEnded}
            onError={handleAudioError}
            preload="metadata"
          />
        )}
      </Card.Body>
    </Card>
  );
};

export default VoiceControl; 